import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAddressDetails } from './hooks/useAddressDetails';
import { useDebounceText } from './hooks/useDebounceText';
import { usePredictAddresses } from './hooks/usePredictAddresses';
import {
  ButtonWrapperPrivacy,
  Form,
  InputWrapperMargin,
  TwoFieldWrapper,
  VMarginWrapper
} from './SignUpForm.styled';
import { TermsOfUse } from 'components/TermsOfUse/TermsOfUse';
import { Button } from 'components/v2/Buttons/Button/Button';
import { AutocompleteField } from 'components/v2/Form/AutocompleteField/AutocompleteField';
import { DateField } from 'components/v2/Form/DateField/DateField';
import { PasswordFieldWithHints } from 'components/v2/Form/PasswordFieldWithHints/PasswordFieldWithHints';
import { PhoneFieldBasic } from 'components/v2/Form/PhoneFieldBasic/PhoneFieldBasic';
import { PreferredTimezoneSelectField } from 'components/v2/Form/PreferredTimezoneSelectField/PreferredTimezoneSelectField';
import { SelectField } from 'components/v2/Form/SelectField/SelectField';
import { SelectWithOptions } from 'components/v2/Form/SelectWithOptions/SelectWithOptions';
import { TextField } from 'components/v2/Form/TextField/TextField';
import { ZipField } from 'components/v2/Form/ZipField/ZipField';
import { Option } from 'components/v2/Inputs/InputWithAutocomplete/InputWithAutocomplete.types';
import { LegalAgreements } from 'components/v2/LegalAgreements/LegalAgreements';
import { Status } from 'components/v2/types';
import { utils } from 'kb-shared';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { MOMENT_TIME_ZONES } from 'kb-shared/utilities/momentHelpers';
import { isInputValid } from 'kb-shared/utilities/validation';
import { SignUpFormProps } from 'screens/Book/components/SignUp.types';
import {
  LOG_SOLERA_ENROLLMENT_ATTEMPT,
  LogSoleraEnrollmentAttemptResponse,
  LogSoleraEnrollmentAttemptVariables
} from 'screens/Solera/SoleraEnrollmentAttempt.graphql';
import { analytics } from 'utilities/analytics';
import {
  dateToString,
  formatDate,
  getUtcDate,
  stringToDate,
  stringToDateUTC
} from 'utilities/formatDate';
import { pageUrl } from 'utilities/pageUrl';
import {
  defaultOption,
  getInitialPatientData,
  hasEmailInput,
  hasPasswordInput,
  isReferralSourceDetailsVisible,
  isReferralInfluencerDetailsVisible,
  isReferralProviderDetailsVisible,
  isUS,
  validateSignUpData
} from 'utilities/userUtil';
import { OptionParams, SIGNUP_TYPE } from 'utilities/userUtil.types';

import { GoToSignIn } from './GoToSignIn/GoToSignIn';
import {
  FETCH_SOLERA_MEMBER_DETAILS,
  QUERY_LABS,
  QUERY_REFERRING_PROVIDERS
} from './SignUpForm.graphql';
import {
  AvailableLabs,
  FetchSoleraMemberDetailsResponse,
  ReferringProviders
} from './SignUpForm.types';
import { sortLabsOptions } from './SignUpForm.utils';
import { SignUpFormModal } from './SignUpFormModal';

const { GENDER_INPUT_DEFAULTS, COUNTRIES_OPTIONS, states } = utils;

export const SignUpForm = (props: SignUpFormProps) => {
  const { loading, onSignUpPatient, signUpType, preselectedLab, patient, signInLinkIsVisible } =
    props;
  const [referralSourcesLoadStatus, setReferralSourcesLoadStatus] = useState<boolean | undefined>(
    undefined
  );
  const [timeZonesLoadStatus, setTimeZonesLoadStatus] = useState<boolean | undefined>(undefined);
  const [patientInterestLoadStatus, setPatientInterestLoadStatus] = useState<boolean | undefined>(
    undefined
  );

  const [patientData, setPatientData] = useState(getInitialPatientData(patient));
  const [emailSentToSegment, setEmailSentToSegment] = useState<string>('');

  const {
    data: availableLabs,
    loading: loadingLabs,
    error: errorLabs
  } = useQuery<AvailableLabs>(QUERY_LABS);
  const { data: availableReferringProviders } = useQuery<ReferringProviders>(
    QUERY_REFERRING_PROVIDERS,
    {
      onError: error => BugTracker.notify(error, 'ReferringProviders')
    }
  );
  const [fetchSoleraMemberDetails] = useMutation<FetchSoleraMemberDetailsResponse>(
    FETCH_SOLERA_MEMBER_DETAILS
  );
  const [logSoleraEnrollmentAttempt] = useMutation<
    LogSoleraEnrollmentAttemptResponse,
    LogSoleraEnrollmentAttemptVariables
  >(LOG_SOLERA_ENROLLMENT_ATTEMPT);

  const debouncedAddress = useDebounceText(patientData.patient.address.address1);
  const debouncedZip = useDebounceText(patientData.patient.address.zipcode);

  const [addressOptions, setAddressOptions] = useState<Array<Option>>([]);
  const [zipOptions, setZipOptions] = useState<Array<Option>>([]);

  const navigate = useNavigate();
  const isBasicUserInfoEventSent = useRef(false);

  const handleBeforeUnload = (e: any) => {
    if (!e) return;
    // there is no need for popup, if patient didn't make any changes on the form
    if (
      JSON.stringify(patientData.patient) === JSON.stringify(getInitialPatientData(patient).patient)
    )
      return;

    e.preventDefault();
    e.returnValue = '';
  };

  useEffect(() => {
    if (analytics) {
      if (signUpType === SIGNUP_TYPE.EMAIL) analytics.page(analytics.PAGES.EMAIL_SIGN_UP);
      if (signUpType === SIGNUP_TYPE.GOOGLE) analytics.page(analytics.PAGES.GOOGLE_SIGN_UP);
      if (signUpType === SIGNUP_TYPE.APPLE) analytics.page(analytics.PAGES.APPLE_SIGN_UP);
      if (signUpType === SIGNUP_TYPE.MEDTRONIC) analytics.page(analytics.PAGES.MEDTRONIC_SIGN_UP);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSoleraUid = () => {
    const params = new URLSearchParams(location.search);
    return params.get('solera_uid');
  };

  useEffect(() => {
    const fetchSoleraData = async () => {
      const uid = getSoleraUid();

      if (!uid) return;

      logSoleraEnrollmentAttempt({
        variables: { enrollmentIdentifier: uid, stage: 'fetch_attempted' }
      });

      try {
        const response = await fetchSoleraMemberDetails({
          variables: { enrollmentIdentifier: uid }
        });

        if (!response?.data?.fetchSoleraMemberDetails?.soleraMemberDetails) {
          BugTracker.notify('No data received from BE', 'FetchSoleraMemberDetails');
          logSoleraEnrollmentAttempt({
            variables: {
              enrollmentIdentifier: uid,
              stage: 'fetch_failed',
              failureReason: 'No data received from backend'
            }
          });
          return;
        }

        const { email, givenName, familyName, address, birthdate, phoneNumber } =
          response.data.fetchSoleraMemberDetails.soleraMemberDetails;

        const stateValue = address?.state || patientData.patient.address.state || '';
        const updatedPatient = {
          ...patientData,
          patient: {
            ...patientData.patient,
            email: email || '',
            firstName: givenName || '',
            lastName: familyName || '',
            phone: phoneNumber || '',
            address: {
              ...patientData.patient.address,
              address1: address?.address1 || patientData.patient.address.address1 || '',
              address2: address?.address2 || patientData.patient.address.address2 || '',
              city: address?.city || patientData.patient.address.city || '',
              state: stateValue,
              zipcode: address?.zipCode || '',
              country: patientData.patient.address.country
            },
            birthdate: birthdate ? formatDate(stringToDate(birthdate), 'YYYY-MM-DD') : ''
          }
        };

        const stateOption = stateValue
          ? states.find(state => state.value === stateValue) || defaultOption
          : defaultOption;
        const errors = validateSignUpData(updatedPatient.patient, signUpType);
        setPatientData({
          ...updatedPatient,
          emailInputValid: !errors.email,
          firstNameInputValid: !errors.firstName,
          lastNameInputValid: !errors.lastName,
          phoneInputValid: !errors.phone,
          zipcodeInputValid: !errors.zipcode,
          stateOption: stateOption
        });

        logSoleraEnrollmentAttempt({
          variables: { enrollmentIdentifier: uid, stage: 'fetch_succeeded' }
        });
      } catch (error) {
        BugTracker.notify(error, 'FetchSoleraMemberDetails');
        logSoleraEnrollmentAttempt({
          variables: {
            enrollmentIdentifier: uid,
            stage: 'fetch_failed',
            failureReason: error instanceof Error ? error.message : 'Unknown error'
          }
        });
      }
    };

    fetchSoleraData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData]);

  const preselectedLabNotEqualDefault = preselectedLab && preselectedLab.id !== defaultOption.value;

  useEffect(() => {
    if (!availableLabs) return;

    // When labs are fetched from the server and preselected lab isn't the default lab, we need to select preselected lab
    if (preselectedLabNotEqualDefault) {
      updatePatientLab({ label: preselectedLab.name, value: preselectedLab.id }, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableLabs]);

  useEffect(() => {
    if (isUS(patientData?.countryOption?.value || '')) {
      updatePatientLab(
        preselectedLabNotEqualDefault
          ? { label: preselectedLab.name, value: preselectedLab.id }
          : defaultOption,
        false
      );
    } else {
      const other = getAllLabOptions()?.find(lab => lab.label === 'Other');
      const labOption = other ? { label: other.label, value: other.value } : defaultOption;
      updatePatientLab(labOption, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData?.countryOption]);

  const checkIfBasicUserInfoSent = () => {
    const { firstName, lastName, email } = patientData.patient;

    const isFirstNameValid = firstName && patientData.firstNameInputValid;
    const isLastNameValid = lastName && patientData.lastNameInputValid;
    const isEmailValid = email && patientData.emailInputValid;

    if (isFirstNameValid && isLastNameValid && isEmailValid && !isBasicUserInfoEventSent.current) {
      isBasicUserInfoEventSent.current = true;
      analytics.track(analytics.EVENTS.ACCOUNT_BASIC_INFO_ENTERED);
    }
  };

  const getAllLabOptions = () => {
    const labsAsOptions = availableLabs?.labs.map(lab => {
      return { label: lab.name, value: lab.id };
    });

    // If there is a preselected lab, but that lab doesn't exist among available labs, manually add preselected lab.
    // This situation happens, when preselected lab isn't patient facing lab.
    if (
      labsAsOptions &&
      preselectedLab != null &&
      labsAsOptions.find(lab => lab.value === preselectedLab.id) == null
    ) {
      labsAsOptions.push({ label: preselectedLab.name, value: preselectedLab.id });
    }
    return labsAsOptions;
  };

  const availableLabOptions = (): OptionParams[] | undefined => {
    const labsAsOptions = getAllLabOptions();
    return labsAsOptions?.sort(sortLabsOptions);
  };

  const updatePatientData = (field: string, event: { currentTarget: { value: string } }) => {
    const value = event.currentTarget.value;
    const updatedPatient = { ...patientData };
    if (
      updatedPatient.patient.address &&
      (field === 'city' ||
        field === 'zipcode' ||
        field === 'state' ||
        field === 'address1' ||
        field === 'address2')
    ) {
      updatedPatient.patient.address = { ...updatedPatient.patient.address, [field]: value };
    } else {
      updatedPatient.patient = { ...updatedPatient.patient, [field]: value };
    }

    const errors = validateSignUpData(updatedPatient.patient, signUpType);
    setPatientData({
      ...updatedPatient,
      [`${field}InputValid`]: !errors[field]
    });
  };

  const autocompleteAddressFields = (fullAddress: string) => {
    // examples of fullAddress are:
    // - 'address without commas'
    // - 'New York, NY, United States'
    // - 'Bul Armory USA LLC, Northwest 7th Place, Miami, FL, United States'
    const addressElements = fullAddress.split(',').map(i => i.trim());

    if (addressElements.length === 0) return;

    const updates: Array<{ field: string; value: string }> = [];

    const address = getAddressFromElements(addressElements);

    if (address) updates.push({ field: 'address1', value: address });

    if (updates.length > 0) autoCompleteAddressDetails(updates);
  };

  const autocompleteZipAndTimezone = (zipAndTimezone: string) => {
    const [zip, timezone, city, stateId] = zipAndTimezone.split(',').map((i: string) => i.trim());
    autoCompleteAddressDetails([
      { field: 'zipcode', value: zip },
      { field: 'timezone', value: timezone },
      { field: 'city', value: city },
      { field: 'stateId', value: stateId }
    ]);
  };

  const autoCompleteAddressDetails = (updates: Array<{ field: string; value: string }>) => {
    const updatedPatient = { ...patientData };

    updates.forEach(update => {
      const field = update.field;
      const value = update.value;

      if (
        updatedPatient.patient.address &&
        (field === 'city' ||
          field === 'zipcode' ||
          field === 'state' ||
          field === 'address1' ||
          field === 'address2')
      ) {
        updatedPatient.patient.address = { ...updatedPatient.patient.address, [field]: value };
      } else if (
        isUS(patientData.patient.address.country) &&
        field === 'timezone' &&
        updatedPatient.preferredTimeZoneOption
      ) {
        const selectedTimeZone = Object.entries(MOMENT_TIME_ZONES).find(
          ([_, tzValue]) => tzValue === value
        )?.[0];

        if (selectedTimeZone) {
          updatedPatient.preferredTimeZoneOption = { value, label: selectedTimeZone };
          updatedPatient.patient.preferredTimezone = selectedTimeZone;
        }
      } else {
        updatedPatient.patient = { ...updatedPatient.patient, [field]: value };
      }

      if (
        isUS(patientData.patient.address.country) &&
        (field === 'state' || updatedPatient.stateOption)
      ) {
        const filteredStates = states.filter(state => state.value === value);
        if (filteredStates.length > 0) {
          const state = filteredStates[0];
          updatedPatient.patient.address.state = state.value;
          updatedPatient.stateOption = state;
        }
      }
    });

    const errors = validateSignUpData(updatedPatient.patient, signUpType);
    const validationUpdates: { [key: string]: boolean } = {};
    updates.forEach(({ field }) => {
      validationUpdates[`${field}InputValid`] = !errors[field];
    });

    setPatientData({
      ...updatedPatient,
      ...validationUpdates
    });
  };

  const updatePatientEmail = (email: string) => {
    const formatted_email = email.replace(/\s/g, '');
    const updatedPatient = {
      ...patientData,
      patient: {
        ...patientData.patient,
        email: formatted_email
      }
    };
    const errors = validateSignUpData(updatedPatient.patient, signUpType);
    setPatientData({
      ...updatedPatient,
      emailInputValid: !errors.email
    });
  };

  const sendEmailToSegment = () => {
    const { email } = patientData.patient;
    const isEmailValid = email && patientData.emailInputValid;

    if (!isEmailValid) return;

    if (!emailSentToSegment || email !== emailSentToSegment) {
      analytics.identify({ email });
      setEmailSentToSegment(email);
    }
  };

  const updatePatientLab = (labOption: { label: string; value: string }, validateLab = true) => {
    const updatedPatient = { ...patientData };
    let newLab = availableLabs?.labs.find(lab => lab.id === labOption.value) || null;
    if (newLab == null && preselectedLab != null) {
      newLab = preselectedLab;
    }
    updatedPatient.patientLab = newLab;
    updatedPatient.patient.labId = newLab ? parseInt(newLab.id) : 0;
    updatedPatient.labOption = labOption;

    const errors = validateSignUpData(updatedPatient.patient, signUpType);
    setPatientData({
      ...updatedPatient,
      patientLabInputValid: !validateLab || !errors.patientLab
    });
  };

  const updatePreferredTimezone = (timeZoneOption: { label: string; value: string }) => {
    const updatedPatient = { ...patientData };
    updatedPatient.patient.preferredTimezone = timeZoneOption.label;
    updatedPatient.preferredTimeZoneOption = timeZoneOption;
    const errors = validateSignUpData(updatedPatient.patient, signUpType);
    setPatientData({
      ...updatedPatient,
      preferredTimezoneInputValid: !errors.preferredTimezone
    });
  };

  const updateGender = (gender: { label: string; value: string }) => {
    const updatedPatient = { ...patientData };
    updatedPatient.patient.gender = gender.value;
    updatedPatient.genderOption = gender;
    setPatientData(updatedPatient);
  };

  const updateCountry = (country: { label: string; value: string }) => {
    const updatedPatient = { ...patientData };

    if (updatedPatient.patient.address == null) return;

    updatedPatient.patient.address.country = country.value;
    updatedPatient.countryOption = country;
    updatedPatient.patient.address.state = '';
    updatedPatient.stateOption = defaultOption;
    updatedPatient.patient.address.zipcode = '';
    updatedPatient.patient.phone = '';
    updatedPatient.patient.address.city = '';
    setPatientData(updatedPatient);
  };

  const updateState = (state: { label: string; value: string }) => {
    const updatedPatient = { ...patientData };

    if (updatedPatient.patient.address == null) return;

    updatedPatient.patient.address.state = state.value;
    updatedPatient.stateOption = state;
    const errors = validateSignUpData(updatedPatient.patient, signUpType);
    setPatientData({
      ...updatedPatient,
      stateInputValid: !errors.state
    });
  };

  const updateBirthdate = (date: string) => {
    const updatedPatient = { ...patientData };
    updatedPatient.patient.birthdate = date;

    const errors = validateSignUpData(updatedPatient.patient, signUpType);
    setPatientData({
      ...updatedPatient,
      birthdateInputValid: !errors.birthdate
    });
  };

  const updateReferralSource = (value: string[]) => {
    const updatedPatient = { ...patientData };
    updatedPatient.patient.referralSource = value[0];

    if (!isReferralSourceDetailsVisible(value[0])) {
      updatedPatient.patient.referralSourceDetails = '';
    }

    const errors = validateSignUpData(updatedPatient.patient, signUpType);
    setPatientData({
      ...updatedPatient,
      referralSourceInputValid: !errors.referralSource,
      referralSourceDetailsInputValid: undefined
    });
  };

  const updatePatientInterest = (value: string[]) => {
    const updatedPatient = { ...patientData };
    updatedPatient.patient.interests = value;

    const errors = validateSignUpData(updatedPatient.patient, signUpType);
    setPatientData({
      ...updatedPatient,
      interestsInputValid: !errors.interests
    });
  };

  const toggleSendSmsAppointmentNotificationFlag = () => {
    const updatedPatient = { ...patientData };
    updatedPatient.patient = {
      ...updatedPatient.patient,
      sendSmsAppointmentNotification: !patientData.patient.sendSmsAppointmentNotification
    };
    setPatientData({ ...updatedPatient });
  };

  const toggleSendMaketingEmailFlag = () => {
    const updatedPatient = { ...patientData };
    updatedPatient.patient = {
      ...updatedPatient.patient,
      sendMarketingEmail: !patientData.patient.sendMarketingEmail
    };
    setPatientData({ ...updatedPatient });
  };

  const onSignUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const errors = validateSignUpData(patientData.patient, signUpType);
    const errorsKeys = Object.keys(errors);
    if (errorsKeys.length) {
      // scroll to an element with invalid input
      document
        .getElementById(`input-${errorsKeys[0]}Input`)
        ?.scrollIntoView({ behavior: 'smooth' });

      setPatientData({
        ...patientData,
        emailInputValid: !errors.email,
        firstNameInputValid: !errors.firstName,
        lastNameInputValid: !errors.lastName,
        address1InputValid: !errors.address1,
        stateInputValid: !errors.state,
        cityInputValid: !errors.city,
        zipcodeInputValid: !errors.zipcode,
        phoneInputValid: !errors.phone,
        birthdateInputValid: !errors.birthdate,
        preferredTimezoneInputValid: !errors.preferredTimezone,
        patientLabInputValid: !errors.patientLab,
        referralSourceInputValid: !errors.referralSource,
        interestsInputValid: !errors.interests,
        referralSourceDetailsInputValid:
          isReferralSourceDetailsVisible(patientData.patient.referralSource) &&
          !errors.referralSourceDetails,
        passwordInputValid: !errors.password
      });
      return;
    }

    if (analytics) {
      if (signUpType === SIGNUP_TYPE.EMAIL) analytics.track(analytics.EVENTS.EMAIL_SIGN_UP_STARTED);
      if (signUpType === SIGNUP_TYPE.GOOGLE)
        analytics.track(analytics.EVENTS.GOOGLE_SIGN_UP_STARTED);
      if (signUpType === SIGNUP_TYPE.APPLE) analytics.track(analytics.EVENTS.APPLE_SIGN_UP_STARTED);
      if (signUpType === SIGNUP_TYPE.MEDTRONIC)
        analytics.track(analytics.EVENTS.MEDTRONIC_SIGN_UP_STARTED);
    }

    const uid = getSoleraUid();
    if (uid) {
      patientData.patient.enrollmentIdentifier = uid;
    }

    onSignUpPatient(patientData.patient, signUpType);
  };

  const hasNameInput = () =>
    signUpType !== SIGNUP_TYPE.GOOGLE ||
    !isInputValid(patient?.firstName, 2) ||
    !isInputValid(patient?.lastName, 2);

  const getLabsLoadStatus = () => {
    if (loadingLabs) return undefined;
    if (errorLabs) return false;
    if (availableLabs && availableLabs.labs.length > 0) return true;
    return false;
  };

  const referringProvidersForMarket = () => {
    if (!availableReferringProviders) return [];

    const selectedMarketId = patientData.patient.labId;
    if (!selectedMarketId)
      return availableReferringProviders.referringProviders.map(provider => ({
        value: provider.name.toUpperCase(),
        label: provider.name.toUpperCase()
      }));

    return availableReferringProviders.referringProviders
      .filter(provider => provider.marketId === selectedMarketId)
      .map(provider => ({
        value: provider.name.toUpperCase(),
        label: provider.name.toUpperCase()
      }));
  };

  const { addressPredictions } = usePredictAddresses(
    debouncedAddress +
      `,${patientData.patient.address.city}, ${patientData.patient.address.state}, ${patientData.patient.address.country}`
  );

  useEffect(() => {
    setAddressOptions(
      addressPredictions?.map(prediction => ({
        value: prediction.description,
        label: prediction.description
      })) || []
    );
  }, [addressPredictions]);

  const { addressDetails } = useAddressDetails(
    debouncedZip,
    patientData.patient.address.city,
    patientData.stateOption?.value ?? ''
  );

  useEffect(() => {
    setZipOptions(
      addressDetails?.map(details => ({
        value: details.zip + `,${details.timezone}` + `,${details.city}` + `,${details.stateId}`,
        label: details.zip
      })) || []
    );
  }, [addressDetails]);

  const getDateInputStatus = (): Status => {
    if (patientData.birthdateInputValid === false) return 'error';

    if (getSoleraUid()) return 'disabled';

    return 'default';
  };

  const getDateInputHelperText = () => {
    if (getDateInputStatus() === 'disabled')
      return 'Birthdate is fixed and must match Solera’s record';

    return patientData.birthdateInputValid === false
      ? 'You must be 18 years or older to schedule a consult.'
      : '';
  };

  return (
    <Form autoComplete="on">
      <div>
        {hasEmailInput(signUpType) && (
          <VMarginWrapper>
            <TextField
              id="emailInput"
              label="EMAIL ADDRESS"
              value={patientData.patient.email}
              onChange={event => {
                updatePatientEmail(event.currentTarget.value);
              }}
              onBlur={() => {
                checkIfBasicUserInfoSent();
                sendEmailToSegment();
              }}
              autoComplete="email"
              spellCheck={false}
              placeholder="janedoe@email.com"
              status={patientData.emailInputValid === false ? 'error' : 'default'}
              helperText={patientData.emailInputValid === false ? 'Please enter a valid email' : ''}
            />
          </VMarginWrapper>
        )}

        {hasNameInput() && (
          <>
            <VMarginWrapper>
              <TextField
                id="firstNameInput"
                label="FIRST NAME"
                value={patientData.patient.firstName}
                onChange={event => {
                  updatePatientData('firstName', event);
                }}
                onBlur={() => checkIfBasicUserInfoSent()}
                autoComplete="given-name"
                spellCheck={false}
                placeholder="First name"
                description="Be sure to enter your full legal name for your medical record."
                status={patientData.firstNameInputValid === false ? 'error' : 'default'}
                helperText={
                  patientData.firstNameInputValid === false ? 'Please enter a valid first name' : ''
                }
              />
            </VMarginWrapper>
            <VMarginWrapper>
              <TextField
                id="lastNameInput"
                label="LAST NAME"
                value={patientData.patient.lastName}
                onChange={event => {
                  updatePatientData('lastName', event);
                }}
                onBlur={() => checkIfBasicUserInfoSent()}
                autoComplete="family-name"
                spellCheck={false}
                placeholder="Last name"
                description="Be sure to enter your full legal name for your medical record."
                status={patientData.lastNameInputValid === false ? 'error' : 'default'}
                helperText={
                  patientData.lastNameInputValid === false ? 'Please enter a valid last name' : ''
                }
              />
            </VMarginWrapper>
          </>
        )}

        <VMarginWrapper>
          <TextField
            id="PREFERRED NAME (OPTIONAL)"
            label="PREFERRED NAME (OPTIONAL)"
            description="Please enter a nickname or preferred name if different from your legal name."
            value={patientData.patient.preferredName}
            onChange={event => updatePatientData('preferredName', event)}
            spellCheck={false}
            placeholder="Preferred name"
          />
        </VMarginWrapper>

        {hasPasswordInput(signUpType) && (
          <PasswordFieldWithHints
            inputId="passwordInput"
            label="PASSWORD"
            placeholder={'Create a password'}
            value={patientData.patient.password || ''}
            onChange={event => updatePatientData('password', event)}
            status={patientData.passwordInputValid === false ? 'error' : 'default'}
          />
        )}

        <VMarginWrapper>
          <SelectField
            label="COUNTRY"
            placeholder="Select country"
            selected={patientData.countryOption}
            onChange={item => updateCountry(item)}
            options={COUNTRIES_OPTIONS}
            id="countryInput"
          />
        </VMarginWrapper>

        <TwoFieldWrapper>
          {patientData.patient.address && isUS(patientData.patient.address.country) ? (
            <InputWrapperMargin>
              <AutocompleteField
                id="zipcodeInput"
                label="ZIP"
                placeholder="ZIP"
                selected={{
                  value: patientData.patient.address.zipcode,
                  label: patientData.patient.address.zipcode
                }}
                options={zipOptions}
                minValueLengthToDisplayOptions={1}
                maxNumberOfDisplayedOptions={5}
                onSelect={e => autocompleteZipAndTimezone(e.value)}
                onChange={event => updatePatientData('zipcode', event)}
                status={patientData.zipcodeInputValid === false ? 'error' : 'default'}
                updateOptions
              />
            </InputWrapperMargin>
          ) : (
            <InputWrapperMargin>
              <ZipField
                id="zipcodeInput"
                label="ZIP"
                value={patientData.patient.address?.zipcode}
                placeholder="ZIP"
                onChange={event => updatePatientData('zipcode', event)}
                status={patientData.zipcodeInputValid === false ? 'error' : 'default'}
                helperText={
                  patientData.zipcodeInputValid === false ? 'Please enter a ZIP code' : ''
                }
              />
            </InputWrapperMargin>
          )}
          <InputWrapperMargin>
            <TextField
              id="cityInput"
              label="CITY"
              placeholder="City"
              value={patientData.patient.address?.city}
              onChange={event => updatePatientData('city', event)}
              status={patientData.cityInputValid === false ? 'error' : 'default'}
              helperText={patientData.cityInputValid === false ? 'Please enter a city name' : ''}
            />
          </InputWrapperMargin>
        </TwoFieldWrapper>

        {patientData.patient.address && isUS(patientData.patient.address.country) ? (
          <VMarginWrapper>
            <SelectField
              id="stateInput"
              label="STATE"
              placeholder="Select state"
              selected={patientData.stateOption}
              options={states}
              onChange={item => updateState(item)}
              status={patientData.stateInputValid === false ? 'error' : 'default'}
              helperText={patientData.stateInputValid === false ? 'Please select a state' : ''}
            />
          </VMarginWrapper>
        ) : (
          <InputWrapperMargin>
            <TextField
              id="stateInput"
              label="STATE"
              value={patientData.patient.address?.state}
              onChange={event => updatePatientData('state', event)}
              placeholder="State"
            />
          </InputWrapperMargin>
        )}

        <VMarginWrapper>
          <AutocompleteField
            id="address1InputAutocomplete"
            label="ADDRESS LINE 1"
            minValueLengthToDisplayOptions={1}
            maxNumberOfDisplayedOptions={5}
            onSelect={e => autocompleteAddressFields(e.label)}
            selected={{
              value: patientData.patient.address.address1,
              label: patientData.patient.address.address1
            }}
            options={addressOptions}
            onChange={event => updatePatientData('address1', event)}
            placeholder="Address"
            status={patientData.address1InputValid === false ? 'error' : 'default'}
            updateOptions
          />
        </VMarginWrapper>
        <VMarginWrapper>
          <TextField
            id="addressLineTwoInput"
            label="ADDRESS LINE 2 (OPTIONAL)"
            value={patientData.patient.address?.address2}
            onChange={event => updatePatientData('address2', event)}
            placeholder="Address"
          />
        </VMarginWrapper>

        <VMarginWrapper>
          <PhoneFieldBasic
            id="phoneInput"
            label="PHONE NUMBER"
            placeholder={
              patientData.patient.address && isUS(patientData.patient.address.country)
                ? '(555) - 555-5555'
                : '(+XXXX)Phone Number'
            }
            value={patientData.patient.phone}
            onChange={event => updatePatientData('phone', event)}
            status={patientData.phoneInputValid === false ? 'error' : 'default'}
            helperText={
              patientData.phoneInputValid === false ? 'You must provide a phone number' : ''
            }
            country={isUS(patientData.patient.address.country) ? 'US' : 'other'}
          />
        </VMarginWrapper>
        <VMarginWrapper>
          <DateField
            id="birthdateInput"
            placeholder="mm/dd/yyyy"
            label="BIRTHDATE"
            value={
              patientData.patient.birthdate
                ? stringToDateUTC(patientData.patient.birthdate)
                : undefined
            }
            onChange={date => updateBirthdate(date ? dateToString(getUtcDate(date)) : '')}
            dateFormat="MM/dd/yyyy"
            autoComplete="off"
            status={getDateInputStatus()}
            helperText={getDateInputHelperText()}
          />
        </VMarginWrapper>

        <VMarginWrapper>
          <SelectField
            id="gender-input"
            label="SEX ASSIGNED AT BIRTH"
            placeholder="Select"
            selected={patientData.genderOption}
            onChange={item => updateGender(item)}
            options={GENDER_INPUT_DEFAULTS}
          />
        </VMarginWrapper>

        <VMarginWrapper>
          <PreferredTimezoneSelectField
            id="preferredTimezoneInput"
            onChange={updatePreferredTimezone}
            selected={patientData.preferredTimeZoneOption?.label}
            label="PREFERRED TIMEZONE"
            placeholder="Select preferred timezone"
            onOptionsLoaded={loadSucceded => setTimeZonesLoadStatus(loadSucceded)}
            status={patientData.preferredTimezoneInputValid === false ? 'error' : 'default'}
            helperText={
              patientData.preferredTimezoneInputValid === false ? 'Please select a timezone.' : ''
            }
          />
        </VMarginWrapper>

        <VMarginWrapper>
          <SelectField
            id="patientLabInput"
            label="KINDBODY HOME LOCATION"
            placeholder="Select city"
            selected={patientData.labOption}
            onChange={updatePatientLab}
            options={availableLabOptions() || []}
            status={patientData.patientLabInputValid === false ? 'error' : 'default'}
            helperText={
              patientData.patientLabInputValid === false ? 'Please select a location.' : ''
            }
          />
        </VMarginWrapper>

        <VMarginWrapper id="referralSource">
          <SelectWithOptions
            inputId="referralSourceInput"
            label="HOW DID YOU HEAR ABOUT US?"
            valueList={[patientData.patient.referralSource]}
            placeholder={'Select one option'}
            lookup_key="patient_referral_source"
            onSelect={updateReferralSource}
            fullMode
            onOptionsLoaded={loadSucceded => setReferralSourcesLoadStatus(loadSucceded)}
            status={patientData.referralSourceInputValid === false ? 'error' : 'default'}
            helperText={
              patientData.referralSourceInputValid === false
                ? 'Please let us know how you heard about us.'
                : ''
            }
          />
        </VMarginWrapper>

        {isReferralInfluencerDetailsVisible(patientData.patient.referralSource) && (
          <VMarginWrapper id="referralSourceDetail">
            <TextField
              id="referralSourceDetailsInput"
              label="PLEASE ENTER THEIR FULL NAME"
              placeholder="First and last name"
              value={patientData.patient.referralSourceDetails}
              onChange={event => updatePatientData('referralSourceDetails', event)}
              status={patientData.referralSourceDetailsInputValid === false ? 'error' : 'default'}
              helperText={
                patientData.referralSourceDetailsInputValid === false
                  ? 'Please let us know their name.'
                  : ''
              }
            />
          </VMarginWrapper>
        )}

        {isReferralProviderDetailsVisible(patientData.patient.referralSource) && (
          <VMarginWrapper id="referralProviderDetail">
            <AutocompleteField
              label="PLEASE ENTER THEIR FULL NAME"
              placeholder="Full name"
              id="referralProviderDetailInput"
              onChange={event => updatePatientData('referralSourceDetails', event)}
              onSelect={option =>
                updatePatientData('referralSourceDetails', {
                  currentTarget: { value: option.value }
                })
              }
              options={referringProvidersForMarket()}
              minValueLengthToDisplayOptions={3}
              maxNumberOfDisplayedOptions={5}
              status={patientData.referralSourceDetailsInputValid === false ? 'error' : 'default'}
              helperText={
                patientData.referralSourceDetailsInputValid === false
                  ? 'Please let us know their full name.'
                  : ''
              }
            />
          </VMarginWrapper>
        )}

        <VMarginWrapper id="interests">
          <SelectWithOptions
            inputId="patientInterestInput"
            label="WHAT ARE YOU INTERESTED IN?"
            placeholder="Select options"
            valueList={patientData.patient.interests}
            lookup_key="patient_interests"
            status={patientData.interestsInputValid === false ? 'error' : 'default'}
            onSelect={updatePatientInterest}
            onOptionsLoaded={loadSucceded => setPatientInterestLoadStatus(loadSucceded)}
            fullMode
            multiSelect
          />
        </VMarginWrapper>

        <LegalAgreements
          sendSmsNotificationSelected={patientData.patient.sendSmsAppointmentNotification}
          sendMarketingEmailSelected={patientData.patient.sendMarketingEmail}
          onSmsNotificationChange={toggleSendSmsAppointmentNotificationFlag}
          onMarketingEmailChange={toggleSendMaketingEmailFlag}
        />

        <ButtonWrapperPrivacy>
          <Button
            label={'CREATE ACCOUNT'}
            onClick={e => {
              if (!loading) onSignUp(e);
            }}
            category="primary"
            isDisabled={loading}
            fullWidth={true}
          />
        </ButtonWrapperPrivacy>
        <TermsOfUse />
        {signInLinkIsVisible && (
          <GoToSignIn
            onSignInClick={() => {
              window.removeEventListener('beforeunload', handleBeforeUnload);
              navigate(pageUrl.login());
            }}
          />
        )}
      </div>

      <SignUpFormModal
        referralSourcesLoadStatus={referralSourcesLoadStatus}
        patientInterestLoadStatus={patientInterestLoadStatus}
        timeZonesLoadStatus={timeZonesLoadStatus}
        locationsLoadStatus={getLabsLoadStatus()}
      />
    </Form>
  );
};

const getAddressFromElements = (addressElements: string[]) => {
  if (addressElements.length >= 4) {
    return addressElements.slice(0, addressElements.length - 3).join(', ');
  } else if (addressElements.length === 1) {
    // Single element without commas - treat as address
    return addressElements[0];
  }
  return '';
};
